import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { ElMessage } from "element-plus";
import qs from "qs";
import { LOGIN_URL } from "@/config";
import { useRuntimeConfig } from "@/stores/runtimeConfig";
import { useUserStore } from "@/stores/user";
import router from "@/routers";
import { useGlobalStore } from "@/stores/global";
import { AxiosCanceler } from "@/api/helper/axiosCancel";

declare module "axios" {
    export interface AxiosRequestConfig {
        loading?: boolean;
        cancel?: boolean;
        noToken?: boolean;
        isNotShowLoading?: boolean;
    }
}

export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
    loading?: boolean;
    cancel?: boolean;
}

const config: AxiosRequestConfig = {
    // 默认地址请求地址，可在 .env.** 文件中修改
    baseURL: import.meta.env.VITE_API_URL as string,
    // 设置超时时间
    timeout: 30000,
    // 跨域时候允许携带凭证
    withCredentials: false,

    paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: "repeat" });
    }
};

// 用于详情页、编辑页的loading
let lastShowTime = 0;
const checkShowLoading = (config: CustomAxiosRequestConfig) => {
    const now = Date.now();
    if (now - lastShowTime < 500) return false;

    const keywords = [
        "customer/sub/invited/list",
        "main/body/invited/list",
        "/mytask/",
        "/file",
        "download",
        "export",
        "paging",
        "-by",
        "-info",
        "all",
        "accounts",
        "message",
        "validate",
        "param",
        "selector",
        "customer/",
        "/login",
        "/capture",
        "/ship-route-info",
        "/ship/keyword",
        "/store-project",
        "/store-outbound/weight",
        "/ocr/business-license" //去除ocr全局loading
    ];
    const hasShow = config.method === "get" && !keywords.some(keyword => config.url?.includes(keyword));
    if (hasShow) {
        lastShowTime = now;
    }
    return hasShow;
    // ["post", "put", "delete"].includes(config.method);
};

const axiosCanceler = new AxiosCanceler();

export class RequestHttp {
    service: AxiosInstance;
    public constructor(config: AxiosRequestConfig) {
        // instantiation
        this.service = axios.create(config);

        /**
         * @description 请求拦截器
         * 客户端发送请求 -> [请求拦截器] -> 服务器
         * token校验(JWT) : 接受服务器返回的 token,存储到 vuex/pinia/本地储存当中
         */
        this.service.interceptors.request.use(
            (config: CustomAxiosRequestConfig) => {
                const globalStore = useGlobalStore();
                if (checkShowLoading(config)) {
                    // 用于详情页、编辑页的首次loading
                    globalStore.startLoading();
                }

                const userStore = useUserStore();
                if (!config.cancel) axiosCanceler.addPending(config);
                // 当前请求不需要显示 loading，在 api 服务中通过指定的第三个参数: { loading: false } 来控制
                if (config.loading) {
                    config.loading = true;
                }
                if (config.loading) globalStore.startLoading();
                if (config.headers && typeof config.headers.set === "function") {
                    const noToken = (config as CustomAxiosRequestConfig & { noToken?: boolean }).noToken;
                    if (!noToken && userStore.token) {
                        config.headers.set("Authorization", userStore.token);
                    }
                }
                return config;
            },
            (error: AxiosError) => {
                return Promise.reject(error);
            }
        );

        /**
         * @description 响应拦截器
         *  服务器换返回信息 -> [拦截统一处理] -> 客户端JS获取到信息
         */
        this.service.interceptors.response.use(
            async response => {
                const globalStore = useGlobalStore();
                globalStore.stopLoading();
                // setTimeout(() => {
                //     hideLoading();
                // }, 200);
                const data = response.data;
                axiosCanceler.removePending(config as CustomAxiosRequestConfig);
                // 登录失效
                if (response.status === 401) {
                    const consoleUIBaseUrl = `${window.location.origin}${useRuntimeConfig().public.publicPath}`;
                    const consoleAdminBaseUrl = consoleUIBaseUrl.endsWith("/console")
                        ? consoleUIBaseUrl.slice(0, -8)
                        : consoleUIBaseUrl;
                    const loginUrl = consoleAdminBaseUrl + LOGIN_URL;
                    window.open(loginUrl, "_self");
                    return Promise.reject(data);
                }
                // noToken 标识第三方接口 没有约定response格式
                const noToken = (response.config as CustomAxiosRequestConfig & { noToken?: boolean }).noToken;
                // 全局错误信息拦截（防止下载文件的时候返回数据流，没有 code 直接报错）
                if (!noToken && data.code && data.code !== "0" && data.code !== "20001003") {
                    ElMessage.error(data.message);
                    return Promise.reject(data);
                }
                // 成功请求（在页面上除非特殊情况，否则不用处理失败逻辑）
                return data;
            },
            async (error: AxiosError) => {
                const globalStore = useGlobalStore();
                globalStore.stopLoading();
                const { response, config } = error;
                axiosCanceler.removePending(config as CustomAxiosRequestConfig);
                // 请求超时 && 网络错误单独判断，没有 response
                if (error.message.includes("timeout")) ElMessage.error("请求超时！请您稍后重试");
                if (error.message.includes("Network Error")) ElMessage.error("网络错误！请您稍后重试");
                // 根据服务器响应的错误状态码，做不同的处理
                if (response) {
                    const { checkStatus } = await import("./helper/checkStatus");
                    await checkStatus(response);
                }
                // 服务器结果都没有返回(可能服务器错误可能客户端断网)，断网处理:可以跳转到断网页面
                if (!window.navigator.onLine) await router.replace("/500");
                return Promise.reject(error);
            }
        );
    }
}

export default new RequestHttp(config).service;
